import './SentimentAnalysis.scss';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import LineChart from '../../../../shared/components/chart/line-chart/LineChart';
import {
  ChatTypeDistribution,
  DateDistribution,
  SentimentDistribution,
} from '../../../../shared/models/context-insights/ContextInsightsResponse';
import { LocalizedText } from '../../../../shared/models/context-insights/MonitorInsightData';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import SimpleBar from 'simplebar-react';
import useScrollFade from '../../../../shared/hooks/scroll/UseScrollFade';
import { useSentimentChartMatrix } from '../../hooks/UseSentimentChartMatrix';
import { PeriodType } from '../../../../shared/types/prompt/PeriodType';
import { buildBucketLabelFormatters } from '../../../../shared/helpers/date/bucketDateLabels';
import NarrativeDrivers from '../narrative-drivers/NarrativeDrivers';
import CustomBarChart from '../../../../shared/components/chart/custom-bar-chart/CustomBarChart';
import { prepareAndGetSentimentStackedBarOptions } from '../../../../shared/components/chart/custom-bar-chart/helpers/CustomBarChartHelper';
import { useLegendState } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseLegendState';
import { renderToStaticMarkup } from 'react-dom/server';
import ChartTooltip from '../../../../shared/components/chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { ChatType, getChatTypeToName } from '../../../../shared/types/monitor/ChatType';
import { Dropdown } from 'primereact/dropdown';
import FilterBox from '../../../../shared/components/filter-box/FilterBox';
import { FilterItem } from '../../../../shared/components/filter-box/models/FilterBoxModel';
import { MonitorEventLogResponse } from '../../../../shared/models/monitor/MonitorEventLogResponse';
import { formatEventsForLineChart } from '../../../../shared/services/monitor/events/EventNameFormatter';

const SENTIMENT_SERIES = [
  { name: 'positive', color: '#45A987' },
  { name: 'neutral', color: '#959596' },
  { name: 'negative', color: '#CD3D44' },
];

export interface InsightPeriodOption {
  label: string;
  value: string;
}

interface SentimentAnalysisProps {
  dateDistribution: DateDistribution[];
  sentimentDistribution: SentimentDistribution[];
  chatTypeDistribution: ChatTypeDistribution[];
  period: PeriodType;
  insights:
    | {
        functional_positioning: LocalizedText;
        value_perception: LocalizedText;
        positioning_gap: LocalizedText;
        business_insight: LocalizedText;
      }
    | undefined;
  eventsData?: MonitorEventLogResponse[] | null;
  isLoading: boolean;
  insightPeriods?: InsightPeriodOption[];
  selectedInsightPeriod?: string | null;
  onInsightPeriodChange?: (value: string | null) => void;
  periodFilterItems?: FilterItem[];
}

const SentimentAnalysis: React.FC<SentimentAnalysisProps> = ({
  dateDistribution,
  sentimentDistribution,
  chatTypeDistribution,
  insights,
  eventsData,
  period,
  isLoading,
  insightPeriods,
  selectedInsightPeriod,
  onInsightPeriodChange,
  periodFilterItems,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'pl';

  const chartEvents = useMemo(
    () => formatEventsForLineChart(eventsData),
    [eventsData, i18n.language]
  );

  const [activeView, setActiveView] = useState<'overTime' | 'byModel'>('overTime');
  const { scrollRef, isAtTop, isAtBottom, handleScroll } = useScrollFade();

  const sentimentChartData = useSentimentChartMatrix(dateDistribution, period);

  // Locale + period aware date labels: compact range on the axis, full range
  // (with year) in the tooltip. The matrix keeps its raw ISO keys/ranges.
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  const sentimentLineChartHeaders = useMemo(
    () => ['date', t('positive'), t('neutral'), t('negative')],
    [t]
  );

  const handleSentimentLegendClick = useCallback(() => {}, []);

  const modelChartData = useMemo(() => {
    const headers = ['model', t('positive'), t('neutral'), t('negative')];
    if (!chatTypeDistribution || chatTypeDistribution.length === 0) {
      return [headers];
    }
    const rows = chatTypeDistribution.map((item) => {
      const total = item.sentiment.POSITIVE + item.sentiment.NEUTRAL + item.sentiment.NEGATIVE;

      if (total === 0) {
        return [getChatTypeToName(item.chatType as ChatType), 0, 0, 0];
      }

      return [
        getChatTypeToName(item.chatType as ChatType),
        Math.round((item.sentiment.POSITIVE / total) * 100),
        Math.round((item.sentiment.NEUTRAL / total) * 100),
        Math.round((item.sentiment.NEGATIVE / total) * 100),
      ];
    });
    return [headers, ...rows];
  }, [chatTypeDistribution, t]);

  const sentimentSeriesWithTranslatedNames = useMemo(
    () => SENTIMENT_SERIES.map((s) => ({ ...s, name: t(s.name) })),
    [t]
  );

  const modelLegendData = sentimentSeriesWithTranslatedNames.map((s) => ({
    label: s.name,
    color: s.color,
  }));

  const { legendStateMap, handleLegendClick } = useLegendState(modelLegendData.map((i) => i.label));

  const filteredModelChartData = useMemo(() => {
    const headers = modelChartData[0];
    const activeIndices = headers.reduce<number[]>((acc, h, i) => {
      if (i === 0 || legendStateMap.get(h as string) !== false) {
        acc.push(i);
      }
      return acc;
    }, []);
    return modelChartData.map((row) => activeIndices.map((i) => row[i]));
  }, [modelChartData, legendStateMap]);

  const formatter = (params: any) => {
    const valueIndex = params.encode?.x?.[0];
    const enrichedParams = {
      ...params,
      data: [params.seriesName, valueIndex !== undefined ? params.data[valueIndex] : params.data[1]],
    };
    return renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={enrichedParams} />);
  };

  const modelChartOptions = useMemo(() => {
    const activeSeriesNames = filteredModelChartData[0].slice(1) as string[];
    const filteredSeriesColors = sentimentSeriesWithTranslatedNames.filter((s) =>
      activeSeriesNames.includes(s.name)
    );
    return prepareAndGetSentimentStackedBarOptions(
      formatter,
      filteredModelChartData,
      filteredSeriesColors
    );
  }, [filteredModelChartData, sentimentSeriesWithTranslatedNames]);

  const insightItems = useMemo(() => {
    if (!insights) return [];
    return [
      {
        key: 'functional_positioning',
        label: t('contextAnalysis.functionalPositioning'),
        data: insights.functional_positioning,
      },
      {
        key: 'value_perception',
        label: t('contextAnalysis.valuePerception'),
        data: insights.value_perception,
      },
      {
        key: 'positioning_gap',
        label: t('contextAnalysis.positioningGap'),
        data: insights.positioning_gap,
      },
      {
        key: 'business_insight',
        label: t('contextAnalysis.businessInsight'),
        data: insights.business_insight,
      },
    ].filter((item) => item.data && item.data[currentLang]);
  }, [insights, currentLang, t]);

  const barChartHeight = useMemo(() => {
    const rowCount = chatTypeDistribution?.length || 1;
    return `${Math.max(rowCount * 55, 500)}px`;
  }, [chatTypeDistribution]);

  const sentimentBarData = useMemo(() => {
    if (!sentimentDistribution || sentimentDistribution.length === 0) {
      return [];
    }

    const colorMap = {
      POSITIVE: '#45A987',
      NEUTRAL: '#959596',
      NEGATIVE: '#CD3D44',
    };

    const order = ['POSITIVE', 'NEUTRAL', 'NEGATIVE'] as const;

    return order.map((sentiment) => {
      const item = sentimentDistribution.find((s) => s.sentiment === sentiment);
      return {
        label: t(sentiment.toLowerCase()),
        value: item ? Math.round(item.percentage) : 0,
        color: colorMap[sentiment],
      };
    });
  }, [sentimentDistribution, t]);

  const noSentimentData = sentimentBarData.every((data) => data.value <= 0);

  return (
    <>
      <div className="stats__panel sentiment-analysis">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {t('contextAnalysis.sentimentOverTime')}
            <CustomTooltip tooltipText="contextAnalysis.sentimentOverTimeTooltip" addMargin={false} />
          </div>
          {activeView === 'overTime' && periodFilterItems && periodFilterItems.length > 0 && (
            <FilterBox items={periodFilterItems} />
          )}
        </div>
        <div className="stats-box stats-box--without-margin stats-box--chart stats-box--chart-long">
          {isLoading ? (
            <div className="stats-box__content">
              <Skeleton className="skeleton--chart skeleton--long">
                <span />
              </Skeleton>
            </div>
          ) : (
            <div className="stats-box__content">
              {dateDistribution && dateDistribution.length > 0 && (
                <div className="stats-box__chart-panel">
                  <div className="switch-box">
                    <span
                      className={`switch-box__item ${
                        activeView === 'overTime' ? 'switch-box__item--active' : ''
                      }`}
                      onClick={() => setActiveView('overTime')}
                    >
                      {t('contextAnalysis.overTime')}
                    </span>
                    <span
                      className={`switch-box__item ${
                        activeView === 'byModel' ? 'switch-box__item--active' : ''
                      }`}
                      onClick={() => setActiveView('byModel')}
                    >
                      {t('contextAnalysis.byAiModel')}
                    </span>
                  </div>
                </div>
              )}
              {activeView === 'overTime' ? (
                dateDistribution && dateDistribution.length > 0 ? (
                  <LineChart
                    legendType={'FLEX_START'}
                    firstData={sentimentChartData}
                    firstColorPalette={['#45A987', '#959596', '#CD3D44']}
                    headers={sentimentLineChartHeaders}
                    onLegendClick={handleSentimentLegendClick}
                    isMax30={false}
                    minValue={0}
                    maxValue={100}
                    formatYAsPercentage={true}
                    chartSizeStyle={{ width: '100%', height: '500px' }}
                    smooth={true}
                    marksData={chartEvents}
                    dateAxisFormatter={dateLabelFormatters.axis}
                    dateTooltipFormatter={dateLabelFormatters.tooltip}
                  />
                ) : (
                  <EmptyPanel />
                )
              ) : chatTypeDistribution && chatTypeDistribution.length > 0 ? (
                <CustomBarChart
                  customEChartsOption={{ eChartsOption: modelChartOptions, legendData: modelLegendData }}
                  legendStateMap={legendStateMap}
                  onLegendClick={handleLegendClick}
                  chartStyle={{ width: '100%', height: barChartHeight }}
                  type={'HORIZONTAL'}
                  notMerge={true}
                />
              ) : (
                <EmptyPanel />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="stats__panel sentiment-analysis-insights">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {t('contextAnalysis.insights')}
            <CustomTooltip tooltipText="contextAnalysis.insightsTooltip" addMargin={false} />
          </div>
          {insights && insightPeriods && onInsightPeriodChange && (
            <Dropdown
              value={selectedInsightPeriod ?? null}
              onChange={(e) => onInsightPeriodChange(e.value ?? null)}
              options={insightPeriods}
              optionLabel="label"
              optionValue="value"
              placeholder={t('contextAnalysis.selectInsightPeriod')}
              className="dropdown dropdown--insight-period"
            />
          )}
        </div>
        {!insights ? (
          <Skeleton
            className={`skeleton--table skeleton--table-${noSentimentData ? '6' : '3'} stats-box`}
          >
            {noSentimentData && (
              <>
                <span />
                <span />
                <span />
                <span />
              </>
            )}
            <span />
            <span />
            <span />
            <span />
          </Skeleton>
        ) : (
          <div>
            <div
              className={`stats-box ${noSentimentData ? 'stats-box--long' : ''} ${
                isAtBottom ? 'stats-box--at-bottom' : ''
              } ${isAtTop ? 'stats-box--at-top' : ''}`}
            >
              <SimpleBar
                className="stats-box__scroll-box"
                style={{ maxWidth: '100%' }}
                autoHide={false}
                scrollableNodeProps={{ ref: scrollRef, onScroll: handleScroll }}
              >
                <div className="insight-list">
                  {insightItems.map((item) => (
                    <div key={item.key} className="insight-list__row">
                      <div className="insight-list__ico-box">
                        <span className="ico ico--eye ico--xxs" />
                      </div>
                      <div className="insight-list__content">
                        <div className="insight-list__label">{item.label}</div>
                        <div className="insight-list__text">{item.data[currentLang]}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </SimpleBar>
            </div>
          </div>
        )}
        {!noSentimentData && <NarrativeDrivers sentimentDistribution={sentimentDistribution} />}
      </div>
    </>
  );
};

export default SentimentAnalysis;
